import { twcls } from '@utils/twcls';
import type {
  CancelOrderMutationVariables,
  GetOrderCancelReasonsQuery
} from '@schema/main';
import { useCallback, useEffect, useState } from 'react';
import type { FormEventHandler, FC } from 'react';

import APIold from '@api/graphqlOld';
import type { LoadingStage } from '@root/src/types';
import { CheckBox, Modal } from '@ui';

interface RemoveOrderModalProps {
  closeModal: () => void;
  confirmCancelOrder: (
    args: Omit<CancelOrderMutationVariables, 'orderId'>
  ) => Promise<void>;
  editOrder: (() => void) | undefined;
}

const denyBtnClassname =
  'border border-solid hover:border-[var(--dark-red)] hover:text-white text-sm h-12 cursor-pointer hover:bg-[var(--main-red)] transition-colors w-full text-center rounded-lg bg-white';

const submitBtnClassname =
  'text-sm h-12 cursor-pointer hover:bg-[var(--main-red)] transition-colors w-full text-center rounded-lg text-white bg-[var(--dark-red)]';

const RemoveOrderModal: FC<RemoveOrderModalProps> = ({
  closeModal,
  confirmCancelOrder,
  editOrder
}) => {
  const [cancelCommentValue, setCancelCommentValue] = useState('');
  const [modalStage, setModalStage] = useState<'initial' | 'reasons'>(
    'reasons'
  );
  const [stage, setStage] = useState<LoadingStage>('initial');
  const [reasons, setReasons] = useState<
    NonNullable<GetOrderCancelReasonsQuery['getOrderCancelReasons']>
  >([]);

  const [selectedReasonId, setSelectedReasonId] = useState<number | null>(null);

  useEffect(() => {
    setStage('loading');
    APIold.getOrderCancelReasons()
      .then(res => {
        setReasons(res.getOrderCancelReasons || []);
        setStage('success');
      })
      .catch(err => {
        if (err) {
          setStage('error');
        }
      });
  }, []);

  const disabled = !selectedReasonId;
  const onSubmitReasons: FormEventHandler<HTMLFormElement> = async (
    e
  ): Promise<void> => {
    e.preventDefault();
    if (disabled) {
      return;
    }
    await confirmCancelOrder({
      reasonId: selectedReasonId,
      cancelComment: cancelCommentValue
    });
  };
  const onForceDeleteBtnClickHandler = useCallback(async () => {
    await confirmCancelOrder({});
  }, [confirmCancelOrder]);

  if (stage !== 'success') {
    return null;
  }
  return (
    <Modal onClose={closeModal} framed placementMobile='bottom-sheet'>
      <div className='w-106 sm:w-full'>
        {modalStage === 'initial' && (
          <>
            {editOrder ? (
              <>
                <p className='mb-5 text-xl font-medium text-center sm:text-base sm:text-start'>
                  Удалить или отредактировать?
                </p>
                <p className='mb-10 text-sm text-center sm:text-start'>
                  Вы можете добавить или удалить товар, применить промокод,
                  изменить дату или адрес доставки, не удаляя заказ.
                </p>
                <div className='grid grid-cols-2 gap-x-3 sm:gap-y-3 sm:flex sm:flex-col'>
                  <button
                    className={denyBtnClassname}
                    onClick={onForceDeleteBtnClickHandler}
                    type='submit'
                  >
                    Все равно удалить
                  </button>
                  <button className={submitBtnClassname} onClick={editOrder}>
                    Редактировать
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className='mb-5 text-xl font-medium text-center sm:text-base sm:text-start'>
                  Удалить этот заказ?
                </p>
                <div className='grid grid-cols-2 gap-x-3 sm:gap-y-3 sm:flex sm:flex-col'>
                  <button
                    className={denyBtnClassname}
                    onClick={onForceDeleteBtnClickHandler}
                    type='submit'
                  >
                    Подтвердить
                  </button>
                  <button className={submitBtnClassname} onClick={closeModal}>
                    Отменить
                  </button>
                </div>
              </>
            )}
          </>
        )}
        {modalStage === 'reasons' && (
          <div>
            <p className='mb-8 text-xl font-medium text-center sm:mb-5 sm:text-base sm:text-start'>
              Отметьте, пожалуйста, причину
            </p>
            <form onSubmit={onSubmitReasons}>
              <ul className={twcls('mb-6', selectedReasonId && 'mb-4')}>
                {reasons.map(item => (
                  <li key={item.id} className='mb-4 text-sm last-of-type:mb-0'>
                    <CheckBox
                      isChecked={selectedReasonId === item.id}
                      onClick={() => {
                        setSelectedReasonId(item.id);
                      }}
                      isRoundCheckbox
                      title={item.title ?? ''}
                    />
                  </li>
                ))}
              </ul>
              {!!selectedReasonId && (
                <textarea
                  onChange={e => {
                    setCancelCommentValue(e.target.value);
                  }}
                  name='cancelComment'
                  className='w-full h-24 px-5 py-3 mb-4 sm:mb-5 text-sm border border-solid border-[var(--dark-grey)] rounded-lg'
                  placeholder='Что пошло не так'
                  rows={3}
                />
              )}
              <button
                disabled={disabled}
                onClick={() => {
                  setModalStage('initial');
                }}
                className={twcls(`
                  cursor-pointer
                  text-white
                  w-full
                  bg-[var(--dark-red)]
                  hover:bg-[var(--main-red)]
                  transition-colors
                  text-sm
                  rounded-lg
                  py-3.5
                  disabled:hover:bg-[var(--grey-dark-new)]
                  disabled:bg-[var(--grey-dark-new)]`)}
                type='button'
              >
                Отправить
              </button>
            </form>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RemoveOrderModal;
